import { Zap, ArrowRight, Lock } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { cn } from '@/lib/utils';
import { Markdown } from '@/common/components/markdown/Markdown';
import { formatNumber } from '@/common/utils/formatNumber';
import { getActivePrice } from '../../utils/prices';
import { ProductStatusBadge } from '../product-status-badge/ProductStatusBadge';
import type { Product, Variant } from '../../services/schemas';

interface CatalogProductCardProps {
  variant: Variant;
  activeProduct: Product | null;
  isLocked: boolean;
  onClick: () => void;
}

export function CatalogProductCard({
  variant,
  activeProduct,
  isLocked,
  onClick,
}: CatalogProductCardProps) {
  const isActive = activeProduct !== null;
  const priceEntry = getActivePrice(variant.priceSet);
  const pricing = priceEntry ? { annualMonthly: priceEntry.priceAnnual / 12 } : null;

  return (
    <button
      onClick={onClick}
      className={cn(
        'text-left bg-white border border-gray-200 rounded-2xl p-5',
        'hover:border-gray-300 transition-all group flex flex-col h-full'
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex size-10 items-center justify-center rounded-xl bg-blue-50">
          <Zap size={18} className="text-blue-600" />
        </div>

        <div className="flex items-center gap-1.5">
          {isLocked && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-gray-100 text-gray-500 border border-gray-200">
              <Lock size={10} />
              <Trans>Requirements unmet</Trans>
            </span>
          )}
          {activeProduct && <ProductStatusBadge status={activeProduct.status} />}
        </div>
      </div>

      <div className="flex-1 mb-4">
        <div className="text-sm font-semibold text-gray-900 mb-1">{variant.productType.name}</div>
        {variant.productType.description && (
          <Markdown content={variant.productType.description} size="xs" />
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div>
          {pricing ? (
            <>
              <span className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">
                <Trans>From</Trans>
              </span>
              <div className="text-sm font-semibold text-gray-800">
                {formatNumber(pricing.annualMonthly, 'currency', { currency: 'CZK' })}
                <span className="text-xs font-normal text-gray-400">
                  {' '}
                  <Trans>/ mo</Trans>
                </span>
              </div>
            </>
          ) : (
            <span className="text-sm font-semibold text-emerald-600">
              <Trans>Free</Trans>
            </span>
          )}
        </div>

        <span
          className={cn(
            'text-xs font-medium flex items-center gap-1 transition-colors',
            isActive ? 'text-gray-400' : 'text-blue-600 group-hover:text-blue-700'
          )}
        >
          {isActive ? <Trans>View details</Trans> : <Trans>Learn more</Trans>}
          <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </button>
  );
}
